import { CheckOutlined } from '@ant-design/icons'
import { Alert, App, Button, Divider, Modal, Spin, Tag, Tooltip } from 'antd'
import _ from 'lodash'
import { FC, Fragment, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import useErrorModal from '../../../../../../hooks/api/use-error-modal'
import { useGetColorantsQuery } from '../../../../../../store/api/colorants-api'
import { useGetPantonesQuery } from '../../../../../../store/api/pantones-api'
import {
    useGetPrintJobPaintsQuery,
    useSavePrintJobPaintsMutation,
} from '../../../../../../store/api/print-jobs-api'
import { getDraftIssues } from './helpers/get-draft-issues'
import { getRowPantoneName } from './helpers/get-row-pantone-name'
import { removeEmptyCells } from './helpers/remove-empty-cells'
import { toRowDrafts } from './helpers/to-row-drafts'
import { toSaveBody } from './helpers/to-save-body'
import PantoneColumn from './pantone-column'
import { PaintRowDraft } from './types'

const BodyStyled = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`

const COLUMNS_PER_ROW = 4

const ColumnsStyled = styled.div`
    display: grid;
    grid-template-columns: repeat(${COLUMNS_PER_ROW}, 320px);
    justify-content: center;
    align-items: start;
    gap: 12px;
`

const RowDivider = styled(Divider)`
    margin: 20px 0;
    border-block-start-width: 2px;
`

const FooterStyled = styled.div`
    display: flex;
    justify-content: flex-end;
`

const pantonesPopulate = {
    manufacturer: { fields: ['name'] },
    colorants: { populate: { colorant: { fields: ['name'] } } },
}

const colorantsQueryArg = {
    fields: ['name'],
    sort: ['name:asc'],
    pagination: { page: 1, pageSize: 200 },
}

interface PaintsModalProps {
    printJobDocumentId: string
    onClose: () => void
}

/** Расход красок по формам отгрузки и их подтверждение. */
const PaintsModal: FC<PaintsModalProps> = ({ printJobDocumentId, onClose }) => {
    const { message } = App.useApp()
    const { data, isFetching } = useGetPrintJobPaintsQuery(printJobDocumentId, {
        refetchOnMountOrArgChange: true,
    })
    const { data: colorants } = useGetColorantsQuery(colorantsQueryArg)
    const pantoneNames = useMemo(
        () => _.uniq((data?.rows ?? []).map(getRowPantoneName).filter(Boolean)),
        [data]
    )
    const { data: pantones, isFetching: isPantonesFetching } = useGetPantonesQuery(
        {
            filters: { name: { $in: pantoneNames } },
            populate: pantonesPopulate,
            pagination: { page: 1, pageSize: 1000 },
        },
        { skip: !pantoneNames.length }
    )
    const [savePaints, saveState] = useSavePrintJobPaintsMutation()
    const [drafts, setDrafts] = useState<PaintRowDraft[]>([])
    const [dirty, setDirty] = useState(false)
    const [showErrors, setShowErrors] = useState(false)

    const colorantOptions = useMemo(
        () => (colorants?.data ?? []).map(item => ({ value: item.documentId, label: item.name })),
        [colorants]
    )

    useEffect(() => {
        if (data && (pantones || !pantoneNames.length)) {
            setDrafts(toRowDrafts(data, pantones?.data ?? []))
            setDirty(false)
            setShowErrors(false)
        }
    }, [data, pantones, pantoneNames.length])

    useErrorModal(saveState, 'Ошибка сохранения расхода красок')

    const changeDraft = (index: number) => (value: PaintRowDraft) => {
        setDrafts(drafts.map((item, i) => (i === index ? value : item)))
        setDirty(true)
    }

    const close = async () => {
        if (dirty) {
            await savePaints({
                docId: printJobDocumentId,
                body: toSaveBody(drafts, false),
            }).unwrap()
            message.info('Черновик сохранён, краски нужно подтвердить')
        }
        onClose()
    }

    const confirm = async () => {
        const cleaned = removeEmptyCells(drafts)
        const issues = getDraftIssues(cleaned)
        setDrafts(cleaned)

        if (issues.length) {
            setShowErrors(true)
            message.error(issues.join('; '))
            return
        }

        await savePaints({ docId: printJobDocumentId, body: toSaveBody(cleaned, true) }).unwrap()
        message.success('Краски подтверждены')
        onClose()
    }

    return (
        <Modal
            title={
                <>
                    Краски{' '}
                    {data?.confirmedAt && !dirty ? (
                        <Tag color='success'>Краски подтверждены</Tag>
                    ) : (
                        <Tag color='warning'>Краски не подтверждены</Tag>
                    )}
                </>
            }
            open
            width={1440}
            onCancel={close}
            footer={
                <FooterStyled>
                    <Tooltip title='Подтвердить краски'>
                        <Button
                            type='primary'
                            icon={<CheckOutlined />}
                            loading={saveState.isLoading}
                            onClick={confirm}
                        />
                    </Tooltip>
                </FooterStyled>
            }
        >
            <Spin spinning={isFetching || isPantonesFetching || saveState.isLoading}>
                <BodyStyled>
                    <Alert
                        type='info'
                        showIcon
                        message='Карточки — флексоформы. Сверху пантон, версия, бренд и порции, под ним состав. По умолчанию — рецепт без версии; можно выбрать любую версию. Состав редактируется после нажатия «Новая версия»; если изменить колоранты, при завершении печати сохранится новая версия. Данные можно менять до завершения печати; чтобы завершить печать, подтвердите краски галочкой.'
                    />
                    {_.chunk(
                        drafts.map((draft, index) => ({ draft, index })),
                        COLUMNS_PER_ROW
                    ).map((row, rowIndex) => (
                        <Fragment key={rowIndex}>
                            {rowIndex > 0 && <RowDivider />}
                            <ColumnsStyled>
                                {row.map(({ draft, index }) => (
                                    <PantoneColumn
                                        key={draft.form.documentId}
                                        draft={draft}
                                        manufacturers={data?.manufacturers ?? []}
                                        colorantOptions={colorantOptions}
                                        showErrors={showErrors}
                                        onChange={changeDraft(index)}
                                    />
                                ))}
                            </ColumnsStyled>
                        </Fragment>
                    ))}
                </BodyStyled>
            </Spin>
        </Modal>
    )
}

export default PaintsModal
